import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useRoles } from "../hooks/useUsers";
import type { RoleOption } from "../types";

interface RoleAssignmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentRoleIds: string[];
  onConfirm: (roleIds: string[]) => void;
  isSubmitting?: boolean;
}

export function RoleAssignmentModal({
  open,
  onOpenChange,
  currentRoleIds,
  onConfirm,
  isSubmitting,
}: RoleAssignmentModalProps) {
  const { data: roles = [], isLoading } = useRoles();
  const [selected, setSelected] = useState<string[]>(currentRoleIds);

  useEffect(() => {
    if (open) setSelected(currentRoleIds);
  }, [open, currentRoleIds]);

  const toggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Assign Roles</DialogTitle>
          <DialogDescription>
            Select the roles to assign to this user. Multiple roles can be combined.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-96 space-y-2 overflow-y-auto pr-1">
          {isLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading roles…</p>
          ) : roles.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No roles available.
            </p>
          ) : (
            roles.map((role: RoleOption) => {
              const isChecked = selected.includes(role.id);
              return (
                <label
                  key={role.id}
                  className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
                    isChecked
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-muted/40"
                  }`}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => toggle(role.id)}
                    className="mt-0.5"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{role.name}</span>
                      {role.isSystem && (
                        <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                          System
                        </span>
                      )}
                    </div>
                    {role.description && (
                      <p className="mt-0.5 text-xs text-muted-foreground">{role.description}</p>
                    )}
                  </div>
                </label>
              );
            })
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(selected)}
            disabled={isSubmitting || selected.length === 0}
          >
            {isSubmitting ? "Saving…" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
